/** 원본 보관·재이행 기한의 **축 하나**. `13.1` O-9 (2026-09-23 사용자 확정).
 *
 * ## 이 파일이 답하는 것과 답하지 않는 것
 *
 * 답하는 것은 **보관 축 30일** 하나다. 원본이 사라지는 날이고, **같은 날이 재이행(다시
 * 만들기) 기한의 끝이기도 하다.** 사용자 확정이 그 둘을 한 값으로 묶었다(ⓑⓓ).
 *
 * ⛔ **답하지 않는 것: 환불 축 30일**(`13.1` O-70 · 법무 `L-F41`). **수가 같고 축이 다르다.**
 * 그쪽은 **못 만든 장의 돈이 자동으로 돌아가는 날**을 세고, 기산점도 **그 장을 못 만든 날**
 * 이라 여기와 다르다. ⛔ **그 값을 이 상수로 재사용하지 마라.** 한 상수로 부르면 둘 중
 * 하나를 바꿀 때 다른 하나가 조용히 따라 움직인다(법무 `4.9.1`(4)).
 * **환불 축이 상수를 갖게 되는 날 이름을 `REFUND_*` 로 가른다.**
 *
 * ⛔ **`JOB_LIST_VISIBLE_DAYS`(7) 와도 다르다.** 그것은 **목록에 보이는 동안**이고
 * `DetailJob.retentionExpiresAt` 이 그 값을 들고 있다 — **컬럼 이름이 `retention` 이지만
 * 파기 시각이 아니다**(`lib/jobs.ts` 의 `createJob` 머리말이 그렇게 적어 뒀다).
 * **이 축의 기한을 그 칸에 얹지 마라.** 얹으면 7일과 30일 중 하나가 거짓이 된다.
 *
 * ## 기한 값은 **행에 박는다. 여기서 계산하지 않는다**
 *
 * 이 저장소의 관례가 그것이다(`docs/DATA_DICTIONARY.md` 18.2 · `AuthSession.expiresAt` ·
 * `DetailJob.retentionExpiresAt`). 상수를 코드에 두고 읽을 때마다 `createdAt` 에서 더하면
 * **정책이 바뀌는 날 옛 행의 기한이 소급해서 바뀐다.** 셀러에게 30일이라고 말하고 받은
 * 사진이 나중에 바뀐 상수 때문에 먼저 사라지는 일이 그것이다.
 *
 * ⛔ **그래서 이 파일에 `Date` 산술이 없다.** 값을 만드는 자리는 SQL
 * (`datetime(CURRENT_TIMESTAMP, '+' || ? || ' days')`)이고, `apps/api/src/lib` 전체가 이미
 * 그 규율로 서 있다(`new Date(` 가 한 자리도 없다. 2026-09-24 실측). 여기 있는 것은
 * **상수**와, 이미 박힌 값을 **읽어서 비교하는** 순수 함수뿐이다.
 *
 * ## ⚠️ 이 파일은 **미반영 설계**를 앞질러 세운 것이 아니다
 *
 * ⛔ **`JobImage.sourceRetentionExpiresAt` 컬럼은 아직 없다. 마이그레이션이 승인되지
 * 않았다**(`docs/DATA_DICTIONARY.md` 31장 M-21). **그때까지 아래 함수들에 넘어올 값은
 * `null` 뿐이고, 함수들은 `null` 을 "모른다"로 받아 막는 쪽으로 떨어진다.** 상수와 판정을
 * 먼저 한 자리에 세워 두는 이유는 **서버·웹·앱이 같은 판정을 하게 하기 위해서**이고,
 * `remainingImageSlots` 를 상한 확정과 같은 회차에 세운 것과 같은 취급이다.
 */

/** 원본 보관 기간. **이 날이 지나면 원본이 없고, 없으므로 다시 만들 수 없다.**
 *
 * ⚠️ **동의 여부와 무관하게 같은 값이다**(사용자 확정 ⓑ). **학습에 동의한 셀러도 31일째엔
 * 못 만든다.** 이 값을 동의 여부로 갈라 두 개로 만들면, **동의가 셀러에게 편익을 주는
 * 구조**가 되어 "거부해도 서비스 이용에 제한이 없다"가 거짓이 된다 — 그 순간 학습 동의의
 * 유효성 근거가 무너진다(법무 `4.9.1`(2) · `L-B9`(1)-2). ⛔ **갈래를 만들지 마라.** */
export const SOURCE_RETENTION_DAYS = 30;

/** 위 상수를 SQL 의 `datetime(?, '+' || ? || ' days')` 두 번째 인자로 넘길 때 쓰는 문자열.
 *  숫자를 문자열로 다시 적는 자리를 만들지 않으려고 둔다. */
export const SOURCE_RETENTION_MODIFIER_DAYS = String(SOURCE_RETENTION_DAYS);

/** D1 이 돌려주는 `DATETIME` 문자열 모양(`YYYY-MM-DD HH:MM:SS`, UTC).
 *
 * **이 모양은 사전순 비교가 곧 시간순 비교다.** 자릿수가 고정이고 UTC 라서 그렇다.
 * 그래서 아래 판정들이 `Date` 를 만들지 않는다 — 파싱을 넣는 순간 Hermes(RN)와 Workers 가
 * 같은 문자열을 다르게 읽는 창이 열린다(`new Date("2026-09-24 00:00:00")` 은 런타임마다
 * 갈린다. 이것이 이 파일이 파싱을 거부하는 실질적 이유다). */
export type D1DateTime = string;

/** 이 장을 **아직 다시 만들 수 있는가**(보관 축만 본다).
 *
 * ⭐ **법무 `4.9.1`(2)㉡ 이 요구하는 판정이 이것이다.** 판정 입력이 **기한**이지
 * **원본이 아직 있는가**가 아니다. ⛔ **원본 유무로 판정하면 학습 사본이 원본으로 세어지는
 * 구현이 언제든 생긴다** — 그리고 그 구현이 한 번이라도 돌면 "거부해도 제한이 없다"가 그
 * 기간에 대해 거짓이었던 것이 되고, 되돌릴 수 없다.
 *
 * ⛔ **이 함수만으로 버튼을 그리지 마라.** 장 상태(`DONE`)·작업 상태(처리 중인가)는
 * `canRemakeImage` 가 본다. **이것은 그 판정에 곱해지는 시간 축 한 항**이고, 두 축을 한
 * 함수에 합치지 않는 이유는 `canRetryImage`/`canRemakeImage` 를 가른 이유와 같다.
 *
 * @param expiresAt 그 장의 `sourceRetentionExpiresAt`. **`null` 은 "아직 모른다"이고**
 *   (컬럼이 서기 전, 또는 옛 행) **막는 쪽으로 떨어진다.** 모르는 채로 열어 주면 기한이 지난
 *   원본에 provider 호출이 나간다.
 * @param now 지금. 같은 모양의 문자열이어야 한다(`D1DateTime`).
 */
export function isWithinSourceRetention(
  expiresAt: D1DateTime | null | undefined,
  now: D1DateTime,
): boolean {
  if (typeof expiresAt !== "string" || expiresAt === "") return false;
  return now < expiresAt;
}

/** 한 작업 안에서 **기한이 갈려 있는가**와 그 양끝.
 *
 * ⭐ **법무 `L-C27` 선행조건 1 이 요구하는 것을 화면이 말할 수 있게 하는 자리다.**
 * 장수 상한(15장)이 작업 분할을 강제하면서 **같은 상품의 사진이 다른 날 사라진다.**
 * 셀러가 읽는 문장이 그 사실에서 거짓이 되지 않으려면, 화면이 **하나의 날짜가 아니라
 * 구간**을 손에 쥐고 있어야 한다.
 *
 * ⛔ **낱말은 이 함수가 고르지 않는다.** `design-manager` 소관이고 `docs/PRD.md` 7절 용어
 * 표가 정본이다. 여기서 돌려주는 것은 **날짜 둘과 갈렸는가 하나**뿐이다.
 *
 * - `first` 가 **가장 먼저 사라지는 장**의 기한이다. 이 날이 지나면 **그 작업은 더 이상
 *   전부를 다시 만들 수 없다.**
 * - `last` 가 **가장 늦게 사라지는 장**의 기한이다.
 * - `split` 이 `true` 면 **둘이 다르다.** ⛔ 이때 화면이 날짜 하나만 적으면 그 문장은
 *   어느 쪽을 적어도 절반에 대해 거짓이다.
 *
 * **기한을 모르는 장(`null`)이 하나라도 있으면 `first` 가 `null` 이다.** 모르는 것을
 * 빼고 최솟값을 내면 **"이 날까지는 안전하다"가 거짓이 될 수 있다.**
 */
export function sourceRetentionRange(
  images: readonly { sourceRetentionExpiresAt: D1DateTime | null }[],
): { first: D1DateTime | null; last: D1DateTime | null; split: boolean } {
  if (images.length === 0) return { first: null, last: null, split: false };

  let first: D1DateTime | null = null;
  let last: D1DateTime | null = null;
  let unknown = false;

  for (const img of images) {
    const at = img.sourceRetentionExpiresAt;
    if (typeof at !== "string" || at === "") {
      unknown = true;
      continue;
    }
    if (first === null || at < first) first = at;
    if (last === null || at > last) last = at;
  }

  // 모르는 장이 섞여 있으면 **앞끝을 말하지 않는다**(머리말). 뒤끝은 그대로 둔다 —
  // 그쪽은 "적어도 여기까지는 남는 장이 있다"라 모르는 장이 있어도 거짓이 되지 않는다.
  if (unknown) return { first: null, last, split: true };

  return { first, last, split: first !== last };
}

/** 학습 전용 사본이 **어느 자리에 있는가**. ⛔ **플래그가 아니라 자리를 가리키는 값이다.**
 *
 * ⭐ **법무 `4.9.1`(2)㉠ 이 요구하는 것이 이 구분의 이유다.** 요구는
 * **"재이행 경로가 읽는 자리와 학습 사본이 있는 자리가 같은 객체가 아닐 것"** 이고,
 * **「31일째에 재이행용 원본이 없다」가 코드 경로의 사실**이어야 한다.
 * ⛔ **정책 플래그로 막는 형태여서는 안 된다 — 없는 것은 못 읽지만 막아 둔 것은 풀린다.**
 *
 * 그래서 이 타입은 **"쓸 수 있는가"를 담지 않는다.** 담으면 그 칸이 곧 플래그가 되고,
 * 그 칸을 뒤집는 한 줄이 언제든 재이행 경로를 학습 사본에 붙일 수 있게 된다.
 * **담는 것은 객체가 어느 버킷에 있는가 하나**이고, 판정은 **그 버킷 바인딩을 요청 경로가
 * 들고 있지 않다는 사실**이 한다.
 *
 * ⚠️ **아직 어떤 행도 이 값을 갖지 않는다.** `TrainingAsset` 테이블이 승인 대기다
 * (`docs/DATA_DICTIONARY.md` 31장 M-22).
 */
export type AssetResidence =
  /** 요청 경로가 읽는 자리. R2 `ASSETS` 버킷의 `jobs/<jobId>/source/<contentHash>`.
   *  **재이행이 `head` 하는 자리가 정확히 이것이다**(`routes/jobs.ts` 의 `/remake`). */
  | "REQUEST_PATH"
  /** 학습 전용 자리. **다른 버킷이고 키에 `jobId` 도 내용 해시도 들어가지 않는다.**
   *  ⛔ 키를 원본 키에서 파생되게 만들지 마라 — 파생되면 "없어서 못 읽는다"가
   *  "계산해서 읽을 수 있다"로 바뀐다(31.4). */
  | "TRAINING_ONLY";

/** 학습 전용 사본이 **적격이라고 우리가 판정한 근거**를 행에 남길 때 쓰는 갈래.
 *
 * ⭐ **법무 `4.9.1`(2)㉤ · `L-B9`(4).** **31일째에 사본을 남기는 것 자체가
 * "이 장은 학습에 써도 된다고 우리가 판정했다"는 행위다.** 그 판정의 근거를 **그때**
 * 남기지 않으면 나중에 증명할 수 없고, 증명하지 못하는 학습 이용은 되돌릴 수 없다
 * (`L-X1` 성질).
 *
 * ⛔ **동의 스위치의 현재 값을 근거로 쓰지 마라.** 스위치는 나중에도 바뀐다.
 * 근거는 **업로드 시점에 그 장에 박힌 값**(`JobImage.trainingUseAllowed` ·
 * `trainingConsentDocVersion`)과 **그 시점에 유효했던 `Consent` 행**이다. */
export interface TrainingEligibilityBasis {
  /** 업로드 시점에 그 장에 박힌 동의 여부. `JobImage.trainingUseAllowed`. */
  allowedAtUpload: boolean;
  /** 그때의 동의 문서 판. `JobImage.trainingConsentDocVersion`. **`null` 이면 부적격이다** —
   *  어느 문안에 동의한 것인지 말할 수 없는 동의는 증명할 수 없다. */
  consentDocVersion: string | null;
  /** 근거가 된 `Consent` 행 id. **판정 시점에 잡아 둔다.** 나중에 다시 찾으면 그사이 철회된
   *  행을 못 찾거나 다른 행을 집는다. */
  consentId: string | null;
}

/** 위 근거로 **학습 전용 사본을 남길 수 있는가**. ⛔ **세 칸이 다 서야 한다.**
 *
 * 하나라도 비면 **남기지 않는다.** 남기고 나서 근거를 못 대는 것보다, 안 남기고 학습
 * 표본을 잃는 쪽이 싸다 — **앞은 되돌릴 수 없고 뒤는 다음 셀러로 메워진다.** */
export function isTrainingCopyEligible(basis: TrainingEligibilityBasis): boolean {
  return (
    basis.allowedAtUpload &&
    typeof basis.consentDocVersion === "string" &&
    basis.consentDocVersion !== "" &&
    typeof basis.consentId === "string" &&
    basis.consentId !== ""
  );
}
